import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import MergeIcon from '@mui/icons-material/Merge';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import AutocompleteSelect from '../../Widgets/AutocompleteSelect/AutocompleteSelect';

const MySwal = withReactContent(Swal);

const mergeArrowFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

// Function to calculate the Levenshtein distance between two strings
const levenshteinDistance = (a, b) => {
  const matrix = Array.from({ length: a.length + 1 }, () => []);
  for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[a.length][b.length];
};

// Function to calculate similarity score between two strings (higher = more similar)
const getSimilarityScore = (name1, name2) => {
  const a = name1 || '';
  const b = name2 || '';
  if (!a.length && !b.length) return 0;
  const distance = levenshteinDistance(a.toLowerCase(), b.toLowerCase());
  const maxLength = Math.max(a.length, b.length);
  if (maxLength === 0) return 0;
  if (distance === maxLength) return -1; // completely different
  return 1 - distance / maxLength;
};

const formatSortCodeLabel = (sc) => {
  if (!sc) return '-';
  return `${sc.sortCodeName ?? ''} - ${sc.sortCodeId ?? ''}`;
};

const MergeSortCodes = ({ isVisible, onClose, sortCodesData, handleBatchMerge, handleSortCodeMerge }) => {
  const { t } = useTranslation();

  const [processedSortCodesData, setProcessedSortCodesData] = useState([]);
  const [mergeOperations, setMergeOperations] = useState([]);
  const [deleteOldCodes, setDeleteOldCodes] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [operationsSearch, setOperationsSearch] = useState('');
  const [showMiniModal, setShowMiniModal] = useState(false);

  // Process sortCodesData to add value and label
  useEffect(() => {
    if (sortCodesData && sortCodesData.length > 0) {
      const transformedData = sortCodesData.map(sortCode => ({
        ...sortCode,
        value: sortCode.sortCodeId,
        label: `${sortCode.sortCodeName} - ${sortCode.sortCodeId}`,
      }));
      setProcessedSortCodesData(transformedData);
    } else {
      setProcessedSortCodesData([]);
    }
  }, [sortCodesData]);

  // Reset operations when modal opens/closes
  useEffect(() => {
    if (isVisible) {
      setMergeOperations([]);
      setOperationsSearch('');
    }
  }, [isVisible]);

  const allSelectedIds = useMemo(() => {
    const set = new Set();
    mergeOperations.forEach(op => {
      if (op.baseSortCode?.sortCodeId) set.add(op.baseSortCode.sortCodeId);
      op.sortCodesToMerge.forEach(sc => set.add(sc.sortCodeId));
    });
    return set;
  }, [mergeOperations]);

  // Options for each operation's base selector (exclude codes already used elsewhere)
  const availableBaseMap = useMemo(() => {
    const map = {};
    mergeOperations.forEach(op => {
      const currentBaseId = op.baseSortCode?.sortCodeId;
      map[op.id] = processedSortCodesData.filter(sc =>
        sc.sortCodeId === currentBaseId || !allSelectedIds.has(sc.sortCodeId)
      );
    });
    return map;
  }, [mergeOperations, processedSortCodesData, allSelectedIds]);

  // Options for each operation's "add to merge" selector
  const availableToMergeMap = useMemo(() => {
    const map = {};
    mergeOperations.forEach(op => {
      const currentMergeIds = new Set(op.sortCodesToMerge.map(sc => sc.sortCodeId));
      map[op.id] = processedSortCodesData.filter(sc =>
        currentMergeIds.has(sc.sortCodeId) || !allSelectedIds.has(sc.sortCodeId)
      );
    });
    return map;
  }, [mergeOperations, processedSortCodesData, allSelectedIds]);

  // Client-side similar sort codes for each operation's base
  const similarSortCodesMap = useMemo(() => {
    const map = {};
    mergeOperations.forEach(op => {
      if (op.baseSortCode?.sortCodeId) {
        const base = processedSortCodesData.find(sc => sc.sortCodeId === op.baseSortCode.sortCodeId);
        if (base) {
          map[op.id] = processedSortCodesData
            .filter(sc => !allSelectedIds.has(sc.sortCodeId))
            .map(sc => ({ ...sc, similarityScore: getSimilarityScore(base.sortCodeName, sc.sortCodeName) }))
            .filter(sc => sc.similarityScore > 0.35)
            .sort((a, b) => b.similarityScore - a.similarityScore)
            .slice(0, 10);
        }
      }
    });
    return map;
  }, [mergeOperations, processedSortCodesData, allSelectedIds]);

  const addNewOperation = useCallback(() => {
    setMergeOperations(prev => [...prev, { id: Date.now(), baseSortCode: null, sortCodesToMerge: [] }]);
  }, []);

  const removeOperation = useCallback((id) => {
    setMergeOperations(prev => prev.filter(op => op.id !== id));
  }, []);

  const updateOperation = useCallback((id, field, value) => {
    setMergeOperations(prev => prev.map(op => (op.id === id ? { ...op, [field]: value } : op)));
  }, []);

  const promoteToBase = useCallback((operationId, sortCode) => {
    setMergeOperations(prev => prev.map(op => {
      if (op.id !== operationId) return op;
      const oldBase = op.baseSortCode;
      const newToMerge = op.sortCodesToMerge.filter(sc => sc.sortCodeId !== sortCode.sortCodeId);
      if (oldBase) newToMerge.push(oldBase);
      return { ...op, baseSortCode: sortCode, sortCodesToMerge: newToMerge };
    }));
  }, []);

  const addSimilarToMerge = (operationId, sortCode) => {
    const operation = mergeOperations.find(op => op.id === operationId);
    if (operation && !operation.sortCodesToMerge.find(sc => sc.sortCodeId === sortCode.sortCodeId)) {
      updateOperation(operationId, 'sortCodesToMerge', [...operation.sortCodesToMerge, sortCode]);
    }
  };

  const getTotalToMerge = () => mergeOperations.reduce((total, op) => total + op.sortCodesToMerge.length, 0);

  const filteredOperations = useMemo(() => {
    const q = (operationsSearch || '').trim().toLowerCase();
    if (!q) return mergeOperations;
    return mergeOperations.filter(op => {
      const base = op.baseSortCode;
      const baseMatch = base && (String(base.sortCodeName || '').toLowerCase().includes(q) || String(base.sortCodeId || '').toLowerCase().includes(q));
      const mergeMatch = (op.sortCodesToMerge || []).some(sc =>
        String(sc.sortCodeName || '').toLowerCase().includes(q) || String(sc.sortCodeId || '').toLowerCase().includes(q)
      );
      return baseMatch || mergeMatch;
    });
  }, [mergeOperations, operationsSearch]);

  const validateOperations = () => {
    const errors = [];
    if (mergeOperations.length === 0) {
      errors.push(t('NoMergeOperationsDefined') || 'No merge operations defined');
      return errors;
    }
    mergeOperations.forEach((op, index) => {
      if (!op.baseSortCode) {
        errors.push(`${t('Operation')} ${index + 1}: ${t('TargetSortCodeNotSelected') || 'Target sort code not selected'}`);
      }
      if (op.sortCodesToMerge.length === 0) {
        errors.push(`${t('Operation')} ${index + 1}: ${t('NoSortCodesSelectedToMerge') || 'No sort codes selected to merge'}`);
      }
      // Locked (non-editable) codes are allowed: they merge but are never deleted.
    });
    return errors;
  };

  const showMergeConfirmation = async () => {
    const errors = validateOperations();
    if (errors.length > 0) {
      MySwal.fire({
        title: t('ValidationErrors') || 'Validation Errors',
        html: errors.map(err => `<div style="text-align: left; margin: 5px 0;">• ${err}</div>`).join(''),
        icon: 'error',
        confirmButtonText: t('Close') || 'Close',
      });
      return false;
    }

    const lockedCount = mergeOperations.reduce(
      (n, op) => n + op.sortCodesToMerge.filter(sc => sc.isEditable === false).length,
      0
    );
    const lockedNote = lockedCount > 0
      ? `<div style="margin-top: 10px; padding: 8px; background-color: #fff3cd; border-radius: 6px; text-align: right; direction: rtl;">
           <strong style="color: #856404; font-size: 12px;">🔒 ${t('LockedSortCodesWontBeDeleted') || 'Locked sort codes will be merged but not deleted.'}</strong>
         </div>`
      : '';

    const totalOps = mergeOperations.length;
    const totalCodes = getTotalToMerge();

    const result = await MySwal.fire({
      title: t('MergeConfirmation') || 'Merge Confirmation',
      html: `
        <div style="direction: rtl; text-align: right; max-height: 70vh; overflow-y: auto;">
          <div style="margin-bottom: 10px; text-align: center;">
            <h3 style="margin: 5px 0; font-size: 16px;">${t('YouAreAboutToMerge') || 'You are about to merge'}</h3>
            <p style="font-size: 14px; color: #1976d2; margin: 5px 0;">
              <strong>${totalOps}</strong> ${t('TargetSortCode') || 'target sort codes'} •
              <strong>${totalCodes}</strong> ${t('SortCodesToMerge') || 'sort codes to merge'}
            </p>
          </div>
          <div style="background-color: #e3f2fd; padding: 8px; border-radius: 6px; margin: 8px 0; max-height: 50vh; overflow-y: auto;">
            ${mergeOperations.map((op, index) => `
              <div style="margin: 6px 0; padding: 8px; background: white; border-radius: 4px; text-align: right; direction: rtl;">
                <strong style="font-size: 14px;">${t('Operation')} ${index + 1}:</strong><br/>
                <div style="margin: 6px 0;">
                  <span style="color: green; font-weight: bold; font-size: 13px;">✓ ${t('TargetSortCode') || 'Target'}:</span>
                  <span style="margin-right: 8px; font-size: 12px;">${op.baseSortCode.sortCodeName} (${op.baseSortCode.sortCodeId})</span>
                </div>
                <div style="margin: 6px 0;">
                  <span style="color: orange; font-weight: bold; font-size: 13px;">← ${t('SortCodesToMerge') || 'Sort codes to merge'} (${op.sortCodesToMerge.length}):</span><br/>
                  ${op.sortCodesToMerge.map(sc => `
                    <span style="margin-right: 15px; display: block; margin-top: 3px; font-size: 12px;">
                      • ${sc.sortCodeName} (${sc.sortCodeId})${sc.isEditable === false ? ' 🔒' : ''}
                    </span>
                  `).join('')}
                </div>
              </div>
            `).join('')}
          </div>
          ${lockedNote}
          <div style="margin-top: 10px; padding: 8px; background-color: #f8d7da; border-radius: 6px; text-align: right; direction: rtl;">
            <strong style="color: #721c24; font-size: 13px;">⚠️ ${t('ThisActionCannotBeUndone') || 'This action cannot be undone'}</strong>
            <p style="margin-top: 5px; font-size: 12px;">${t('AllDocumentsWillBeUpdated') || 'All documents and entries will be updated accordingly'}</p>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('ProceedWithMerge') || 'Proceed with Merge',
      cancelButtonText: t('CancelMerge') || 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      width: '90%',
      maxWidth: '700px',
      customClass: { popup: 'swal2-rtl', htmlContainer: 'swal2-rtl' },
    });

    return result.isConfirmed;
  };

  const handleConfirmMerge = async () => {
    const confirmed = await showMergeConfirmation();
    if (!confirmed) return;

    setIsProcessing(true);
    try {
      const batchMergeData = {
        mergeOperations: mergeOperations.map(op => ({
          primarySortCodeId: op.baseSortCode.sortCodeId,
          secondarySortCodeIds: op.sortCodesToMerge.map(sc => sc.sortCodeId),
        })),
        deleteOldCodes,
      };

      if (typeof handleBatchMerge === 'function') {
        await handleBatchMerge(batchMergeData);
      } else if (typeof handleSortCodeMerge === 'function') {
        // Backward-compatible fallback: run each pair through the single merge handler.
        for (const op of mergeOperations) {
          for (const sc of op.sortCodesToMerge) {
            await handleSortCodeMerge(op.baseSortCode.sortCodeId, sc.sortCodeId);
          }
        }
      }

      setTimeout(() => handleClose(), 1500);
    } catch (error) {
      MySwal.fire({
        title: t('Error') || 'Error',
        text: t('FailedToBatchMerge') || 'Failed to perform batch merge',
        icon: 'error',
        confirmButtonText: t('Close') || 'Close',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setMergeOperations([]);
    setOperationsSearch('');
    setShowMiniModal(false);
    onClose();
  };

  // Add an operation from the "similar names" mini modal (base + its similar codes)
  const addOperationFromSimilar = (baseSortCodeId, similarIds = []) => {
    const base = processedSortCodesData.find(sc => sc.sortCodeId === baseSortCodeId);
    if (!base) return;
    const toMerge = similarIds
      .map(id => processedSortCodesData.find(sc => sc.sortCodeId === id))
      .filter(Boolean);

    setMergeOperations(prev => {
      const emptyIdx = prev.findIndex(op => !op.baseSortCode && op.sortCodesToMerge.length === 0);
      if (emptyIdx !== -1) {
        const updated = [...prev];
        updated[emptyIdx] = { ...updated[emptyIdx], baseSortCode: base, sortCodesToMerge: toMerge };
        return updated;
      }
      return [...prev, { id: Date.now(), baseSortCode: base, sortCodesToMerge: toMerge }];
    });
    toast.success(t('SortCodeMergeOperationAdded') || 'Merge operation added', { position: 'top-center', autoClose: 2000 });
    setShowMiniModal(false);
  };

  // Groups of sort codes with similar names (for the mini modal)
  const similarNameGroups = useMemo(() => {
    // Exclude sort codes already picked in an operation (as target or to-merge).
    const available = processedSortCodesData.filter(sc => !allSelectedIds.has(sc.sortCodeId));
    return available
      .map((sortCode) => {
        const similar = available
          .filter(other => other.sortCodeId !== sortCode.sortCodeId)
          .map(other => ({ ...other, similarityScore: getSimilarityScore(sortCode.sortCodeName, other.sortCodeName) }))
          .filter(other => other.similarityScore > 0.75)
          .sort((a, b) => b.similarityScore - a.similarityScore)
          .slice(0, 5);
        const highestSimilarityScore = similar.length > 0 ? similar[0].similarityScore : 0;
        return { sortCode, similar, highestSimilarityScore };
      })
      .filter(item => item.similar.length > 0)
      .sort((a, b) => b.highestSimilarityScore - a.highestSimilarityScore);
  }, [processedSortCodesData, allSelectedIds]);

  return (
    <>
      <Dialog
        open={isVisible}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{ '& .MuiDialog-paper': { maxHeight: '95vh', direction: 'ltr' } }}
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '2px solid #1976d2', py: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <MergeIcon sx={{ mr: 1, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="bold">
                {t('BatchMergeSortCodes') || t('MergeSortCodes')}
              </Typography>
            </Box>
            <Chip
              label={`${mergeOperations.length} ${t('Operations')} | ${getTotalToMerge()} ${t('SortCodesToMerge')}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, overflowY: 'auto', maxHeight: 'calc(95vh - 200px)' }}>
          {/* Toolbar */}
          <Box sx={{ mb: 1.5, mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Button
              onClick={() => setShowMiniModal(true)}
              variant="outlined"
              endIcon={<ListIcon sx={{ ml: 1 }} />}
              disabled={isProcessing}
              sx={{ textTransform: 'none' }}
            >
              {t('ViewSortCodesWithSimilarNames')}
            </Button>
          </Box>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 1.5 }}>
            {t('LockedSortCodesWontBeDeleted') || 'Locked sort codes will be merged but not deleted.'}
          </Alert>

          {/* Delete option */}
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={deleteOldCodes}
                  onChange={(e) => setDeleteOldCodes(e.target.checked)}
                  color="primary"
                  disabled={isProcessing}
                />
              }
              disabled={isProcessing}
              label={
                <Box display="flex" alignItems="center">
                  <Typography>{t('DeleteMergedSortCodes') || t('DeleteMergedCards')}</Typography>
                  <Tooltip title={t('DeleteMergedSortCodesTooltip') || t('DeleteMergedCardsTooltip') || ''}>
                    <InfoIcon sx={{ ml: 1, fontSize: 18, color: 'gray' }} />
                  </Tooltip>
                </Box>
              }
            />
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Search operations */}
          {mergeOperations.length > 0 && (
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder={t('SearchSortCodeOperations') || 'Search operations'}
                value={operationsSearch}
                onChange={(e) => setOperationsSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 360 }}
              />
              {operationsSearch.trim() && (
                <Typography variant="caption" color="textSecondary">
                  {filteredOperations.length} / {mergeOperations.length} {t('Operations')}
                </Typography>
              )}
            </Box>
          )}

          {/* Operations */}
          {mergeOperations.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: '#fafafa', borderStyle: 'dashed' }}>
              <MergeIcon
                sx={{ fontSize: 48, color: 'primary.main', mb: 1, animation: `${mergeArrowFloat} 1.6s ease-in-out infinite` }}
              />
              <Typography variant="body1" color="textSecondary" gutterBottom>
                {t('NoOperationsYet') || 'No merge operations yet'}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                {t('AddSortCodeOperationsHint') || 'Use the similar names view or add a merge manually'}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addNewOperation}
                disabled={isProcessing}
                sx={{ textTransform: 'none' }}
              >
                {t('AddMerge') || 'Add Merge'}
              </Button>
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {filteredOperations.map((operation) => {
                const realOpIndex = mergeOperations.findIndex(op => op.id === operation.id) + 1;
                const similar = similarSortCodesMap[operation.id] || [];
                const hasSimilar = similar.length > 0;

                return (
                  <Paper
                    key={operation.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderColor: operation.baseSortCode ? '#1976d2' : '#e0e0e0',
                      borderWidth: operation.baseSortCode ? 1.5 : 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`#${realOpIndex}`} size="small" color="primary" sx={{ fontWeight: 700, fontSize: 12, height: 24 }} />
                        {operation.baseSortCode && (
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {operation.baseSortCode.sortCodeName}
                            {operation.sortCodesToMerge.length > 0 && ` ← ${operation.sortCodesToMerge.length}`}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title={t('RemoveOperation')}>
                        <IconButton onClick={() => removeOperation(operation.id)} color="error" size="small" disabled={isProcessing}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Target (base) sort code */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', fontSize: 11, mb: 0.5, display: 'block' }}>
                        {t('TargetSortCode')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <AutocompleteSelect
                            options={availableBaseMap[operation.id] ?? []}
                            selectedValues={
                              (availableBaseMap[operation.id] ?? []).find(o => o.value === operation.baseSortCode?.sortCodeId) || null
                            }
                            onChange={(event, newValue) => updateOperation(operation.id, 'baseSortCode', newValue || null)}
                            placeholder={t('SelectTargetSortCode') || t('TargetSortCode')}
                            isMultiple={false}
                            width="100%"
                            disabled={isProcessing}
                          />
                        </Box>
                        {operation.baseSortCode?.isEditable === false && (
                          <Chip size="small" icon={<LockIcon sx={{ fontSize: '14px !important' }} />} label={t('Locked') || 'Locked'} sx={{ fontSize: 10, height: 22 }} />
                        )}
                      </Box>
                    </Box>

                    {/* Sort codes to merge */}
                    {operation.sortCodesToMerge.length > 0 && (
                      <Box sx={{ mb: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#e65100', fontSize: 11 }}>
                            {t('SortCodesToMerge')} ({operation.sortCodesToMerge.length})
                          </Typography>
                          {operation.sortCodesToMerge.length > 1 && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => updateOperation(operation.id, 'sortCodesToMerge', [])}
                              disabled={isProcessing}
                              sx={{ textTransform: 'none', fontSize: 10, py: 0, minHeight: 0, lineHeight: 1.5 }}
                            >
                              {t('RemoveAll') || 'Remove all'}
                            </Button>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {operation.sortCodesToMerge.map(sc => (
                            <Tooltip
                              key={sc.sortCodeId}
                              arrow
                              title={
                                <Box sx={{ fontSize: 12 }}>
                                  <strong>{sc.sortCodeName}</strong><br />
                                  {t('SortCode ID')}: {sc.sortCodeId}
                                  {sc.isEditable === false ? <><br />🔒 {t('LockedWontBeDeleted') || 'Locked - will not be deleted'}</> : null}
                                  <br />{t('ClickToSetAsBase') || 'Click to set as target'}
                                </Box>
                              }
                            >
                              <Chip
                                size="small"
                                label={formatSortCodeLabel(sc)}
                                color={sc.isEditable === false ? 'default' : 'warning'}
                                variant="filled"
                                icon={sc.isEditable === false ? <LockIcon sx={{ fontSize: '14px !important' }} /> : <StarIcon sx={{ fontSize: '14px !important' }} />}
                                onClick={() => !isProcessing && promoteToBase(operation.id, sc)}
                                onDelete={isProcessing ? undefined : () => {
                                  updateOperation(operation.id, 'sortCodesToMerge', operation.sortCodesToMerge.filter(c => c.sortCodeId !== sc.sortCodeId));
                                }}
                                deleteIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: 11, height: 26, cursor: 'pointer', maxWidth: 320, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Add sort codes to merge */}
                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', fontSize: 11, mb: 0.5, display: 'block' }}>
                        {t('AddSortCodesToMerge') || t('AddCardsToMerge')}
                      </Typography>
                      <AutocompleteSelect
                        options={availableToMergeMap[operation.id] ?? []}
                        selectedValues={null}
                        onChange={(event, newValue) => {
                          if (newValue && !operation.sortCodesToMerge.find(sc => sc.sortCodeId === newValue.sortCodeId)) {
                            updateOperation(operation.id, 'sortCodesToMerge', [...operation.sortCodesToMerge, newValue]);
                          }
                        }}
                        placeholder={t('SearchAndAddSortCode') || t('SearchAndAddCard') || 'Search sort code to add...'}
                        isMultiple={false}
                        width="100%"
                        disabled={isProcessing}
                      />
                    </Box>

                    {/* Suggested similar sort codes */}
                    {operation.baseSortCode && hasSimilar && (
                      <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #e0e0e0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: '#888', mb: 0.5, display: 'block' }}>
                          {t('SuggestedSimilarSortCodes') || t('MostSimilarSortCodeNames')}:
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {similar.slice(0, 8).map(sc => {
                            const alreadyAdded = operation.sortCodesToMerge.some(c => c.sortCodeId === sc.sortCodeId);
                            return (
                              <Tooltip
                                key={sc.sortCodeId}
                                arrow
                                title={
                                  <Box sx={{ fontSize: 12 }}>
                                    <strong>{sc.sortCodeName}</strong><br />
                                    {t('SortCode ID')}: {sc.sortCodeId}<br />
                                    {t('similar')}: {(sc.similarityScore * 100).toFixed(0)}%
                                    {alreadyAdded ? <><br /><em>{t('AlreadyAdded') || 'Already added'}</em></> : null}
                                  </Box>
                                }
                              >
                                <Chip
                                  size="small"
                                  icon={alreadyAdded ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : undefined}
                                  label={`${formatSortCodeLabel(sc)} • ${(sc.similarityScore * 100).toFixed(0)}%`}
                                  variant={alreadyAdded ? 'filled' : 'outlined'}
                                  color={alreadyAdded ? 'success' : 'default'}
                                  onClick={() => !isProcessing && !alreadyAdded && addSimilarToMerge(operation.id, sc)}
                                  sx={{
                                    cursor: isProcessing || alreadyAdded ? 'default' : 'pointer',
                                    fontSize: 11, height: 24,
                                    opacity: alreadyAdded ? 0.7 : 1,
                                    '&:hover': { bgcolor: alreadyAdded ? undefined : '#e3f2fd' },
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}

          {/* Add operation button */}
          {mergeOperations.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addNewOperation}
                disabled={isProcessing}
                sx={{ textTransform: 'none' }}
              >
                {t('AddMerge') || 'Add Merge'}
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ direction: 'ltr', justifyContent: 'space-between', px: 3, py: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" color="textSecondary">
            {t('TotalOperations')}: {mergeOperations.length} | {t('SortCodesToMerge')}: {getTotalToMerge()}
          </Typography>
          <Box>
            <Button
              onClick={handleConfirmMerge}
              variant="contained"
              color="primary"
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <MergeIcon />}
              disabled={isProcessing || mergeOperations.length === 0}
              sx={{ mr: 1 }}
            >
              {isProcessing ? (t('Merging')) : (t('ConfirmMerge'))}
            </Button>
            <Button
              onClick={handleClose}
              variant="outlined"
              startIcon={<ClearIcon />}
              disabled={isProcessing}
            >
              {t('cancelButton')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Mini modal – sort codes with similar names */}
      {showMiniModal && (
        <Dialog
          open={showMiniModal}
          onClose={() => setShowMiniModal(false)}
          maxWidth="sm"
          fullWidth
          sx={{ '& .MuiDialog-paper': { maxHeight: '80vh' } }}
          dir="rtl"
        >
          <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' ,marginBottom: '10px'}}>
            <Box display="flex" alignItems="center" gap={1}>
              <ListIcon />
              <Typography variant="h6" fontWeight="bold">{t('ViewSortCodesWithSimilarNames')}</Typography>
            </Box>
            <IconButton onClick={() => setShowMiniModal(false)} sx={{ color: 'white' }} size="small">
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {similarNameGroups.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="textSecondary">{t('NoSimilarCardsFound') || 'No similar sort codes found'}</Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {similarNameGroups.map(({ sortCode, similar }) => (
                  <Paper
                    key={sortCode.sortCodeId}
                    variant="outlined"
                    onClick={() => addOperationFromSimilar(sortCode.sortCodeId, similar.map(s => s.sortCodeId))}
                    sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f0f7ff', borderColor: '#1976d2' } }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {sortCode.sortCodeName} <span style={{ color: '#888', fontWeight: 400 }}>({sortCode.sortCodeId})</span>
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {similar.map(sc => (
                        <Chip
                          key={sc.sortCodeId}
                          size="small"
                          label={`${formatSortCodeLabel(sc)} • ${(sc.similarityScore * 100).toFixed(0)}%`}
                          variant="outlined"
                          sx={{ fontSize: 10, height: 22 }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowMiniModal(false)} startIcon={<ClearIcon />}>
              {t('Close') || t('cancelButton')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default MergeSortCodes;
